/**
 * 3D航跡の壁の天面に沿って浮かせる「稜線ライン」を、自前のWebGL描画
 * （`maplibregl.CustomLayerInterface`）で引く。
 *
 * ★ なぜfill-extrusionをやめてこれにしたか（利用者からの要望、
 *   2026-09-14）。fill-extrusionは1枚のポリゴンに一律の高さしか
 *   持たせられず、実データの点の間隔なりの「階段」になる。階段を
 *   目立たなくするには区間を細かく分割（細分化）するしかないが、
 *   これは総ポリゴン数を増やし、実機・Playwrightの両方で描画負荷の
 *   上昇（地図を動かすアニメーション中の固まり）を引き起こした
 *   （§164参照、一度実装して撤回した）。
 *
 *   自前のWebGL描画なら、CPU側で点を増やさなくても、実際の点をそのまま
 *   使うだけで点と点の間を滑らかに（直線的に）繋げられる。頂点数は
 *   実データの点数のオーダーのまま（後述の帯化で6倍にはなるが、壁本体の
 *   ポリゴン数とは無関係）なので、追加の描画負荷はごく小さい。
 *
 * ★ 高度は`maplibregl.MercatorCoordinate.fromLngLat(lngLat, altitudeM)`で
 *   実際のメートル高度からメルカトル座標へ変換する（この関数が緯度による
 *   縮尺差・地形との整合を面倒みてくれる）。渡す高度は、既存のfill-extrusion
 *   と全く同じ式（誇張倍率・地形標高の反映）を通した実効高度にすること
 *   （main.ts側で計算してから渡す）。
 *
 * ★★ 重要な落とし穴1（実際に踏んだ、2026-09-15）:
 *   `CustomRenderMethodInput.modelViewProjectionMatrix`は、`MercatorCoordinate`
 *   が返す正規化された[0,1]座標をそのまま受け取る行列では**ない**。
 *   MapLibre本体のソース（`Transform._calcMatrices()`）を直接確認したところ、
 *   `[0,1]`座標をそのまま渡せるのは`_mercatorMatrix`（`worldSize`倍した
 *   別行列、カスタムレイヤーには公開されていない）で、`modelViewProjectionMatrix`
 *   の実体である`_viewProjMatrix`は「`worldSize`（=タイルサイズ512×2^zoom）
 *   ぶん既にスケールされた座標」を前提にしている。これに気付かず
 *   `MercatorCoordinate`の生の値をそのまま行列に掛けたところ、
 *   計算結果のNDC座標が[-1,1]の範囲を大きく外れ、線が完全に描画され
 *   ない（GLエラーは出ない・drawArraysは呼ばれる・データも正しいのに
 *   何も見えない、という一見原因不明の不具合になった）。対策として、
 *   頂点シェーダー側で`u_worldSize`を乗算してから行列を掛ける
 *   （`render()`で`map.transform.worldSize`を毎フレーム渡す。ズームで
 *   変わる値なので頂点バッファ側には焼き込めない）。
 *
 * ★★ 重要な落とし穴1.5（コードレビューで指摘・実際に踏んだ、2026-09-15）:
 *   落とし穴1の対策として「x・y・zすべてに`u_worldSize`を掛ける」実装に
 *   したところ、稜線が壁の天面ではなくほぼ地面付近（壁の底）に描かれる
 *   別の不具合が起きた。原因は**高度（z）だけは`worldSize`を掛けては
 *   いけない**こと。`MercatorCoordinate.fromLngLat(lngLat, altitudeM).z`は
 *   `mercatorZfromAltitude()`（x・yと同じ「正規化メルカトル単位」）を返すが、
 *   `_calcMatrices()`が`_viewProjMatrix`を組み立てる際、x・yはworldSize倍
 *   された「ワールドピクセル単位」を前提にする一方、zには
 *   `scale(m, m, [1, 1, pixelPerMeter])`で**メートル単位の高度をそのまま
 *   受け取り内部でpixelPerMeterを掛ける**変換が既に焼き込まれている
 *   （`pixelPerMeter = mercatorZfromAltitude(1, lat) * worldSize`）。
 *   つまり`modelViewProjectionMatrix`は「x・yはworldSize倍したワールド
 *   ピクセル、zは生のメートル」という混在した単位を前提にしており、
 *   正規化メルカトルzを渡した上でさらにworldSizeを掛けると、高度が
 *   `pixelPerMeter`ぶん二重に縮小される（zoom・緯度によって数十分の一に
 *   なりうる）。対策として、頂点データのzには`MercatorCoordinate.z`では
 *   なく**メートル単位の実効高度をそのまま**入れ、シェーダーでは
 *   x・yだけに`u_worldSize`を掛ける。
 *
 * ★★ 重要な落とし穴2（実際に踏んだ、2026-09-15）:
 *   落とし穴1を直した後、線は正しい位置に描かれるようになったが、
 *   `gl.LINE_STRIP`はほとんどの環境（ANGLE/SwiftShaderのソフトウェア
 *   レンダリングを含む）で線幅が1pxに固定されており
 *   （`gl.lineWidth()`を呼んでも無視される）、壁本体の色とほぼ同化して
 *   通常のズームでは実質見えなかった（利用者から「線が細いのか、
 *   見えない」との指摘）。対策として、1pxの`LINE_STRIP`をやめ、
 *   区間ごとに画面空間で一定ピクセル幅の帯（三角形2枚）を組み立てる
 *   「スクリーン空間の太線」技法に作り直した。各頂点に「自分の位置」
 *   「区間の相手側の位置」「左右どちらにオフセットするか」を持たせ、
 *   頂点シェーダーで両者をクリップ空間へ投影してから画面空間での
 *   進行方向の法線ベクトルを求め、そのぶんだけオフセットする
 *   （ズーム・pitchによらず常に一定ピクセル幅に見える）。
 */

import maplibregl from 'maplibre-gl';
import type { CustomRenderMethodInput, Map as MapLibreMap } from 'maplibre-gl';

/** 稜線を作る1点。effectiveAltMは誇張倍率・地形標高を反映済みの実効高度[m] */
export interface RidgeLinePoint {
  lon: number;
  lat: number;
  effectiveAltM: number;
  /** [r,g,b,a]、各0〜1 */
  color: [number, number, number, number];
}

/** 線の太さ [px]（devicePixelRatio込みの実ピクセル。ズーム・pitchによらず一定） */
const LINE_WIDTH_PX = 3;

const VERTEX_SHADER = `
attribute vec3 a_current;
attribute vec3 a_other;
attribute float a_side;
attribute vec4 a_color;
uniform mat4 u_matrix;
uniform float u_worldSize;
uniform vec2 u_viewportSize;
uniform float u_lineWidthPx;
varying vec4 v_color;

void main() {
  // ★ x・yはworldSize倍のワールドピクセル単位、zは生のメートル単位
  //   （ファイル冒頭の「重要な落とし穴1.5」参照。zにworldSizeを掛けると
  //   行列内部のpixelPerMeter変換と二重にかかってしまう）
  vec4 currentWorld = vec4(a_current.xy * u_worldSize, a_current.z, 1.0);
  vec4 otherWorld = vec4(a_other.xy * u_worldSize, a_other.z, 1.0);
  vec4 clipCurrent = u_matrix * currentWorld;
  vec4 clipOther = u_matrix * otherWorld;

  vec2 halfViewport = u_viewportSize * 0.5;
  vec2 screenCurrent = (clipCurrent.xy / clipCurrent.w) * halfViewport;
  vec2 screenOther = (clipOther.xy / clipOther.w) * halfViewport;

  vec2 dir = screenOther - screenCurrent;
  float len = length(dir);
  // 区間の始点・終点が画面上でほぼ同じ位置（真上から見た1点など）の
  // ときは法線が定義できないため、適当な向き（水平）にしておく
  vec2 normal = len > 0.00001 ? vec2(-dir.y, dir.x) / len : vec2(1.0, 0.0);

  vec2 offsetScreen = normal * (u_lineWidthPx * 0.5) * a_side;
  vec2 offsetClip = (offsetScreen / halfViewport) * clipCurrent.w;

  gl_Position = vec4(clipCurrent.xy + offsetClip, clipCurrent.z, clipCurrent.w);
  v_color = a_color;
}
`;

const FRAGMENT_SHADER = `
precision mediump float;
varying vec4 v_color;
void main() {
  gl_FragColor = v_color;
}
`;

function compileShader(gl: WebGLRenderingContext | WebGL2RenderingContext, type: number, source: string): WebGLShader {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('シェーダーを作成できません');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const log = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(`シェーダーのコンパイルに失敗: ${log}`);
  }
  return shader;
}

/**
 * 複数本の航跡（自機フライト、または受信中の各機）を、1つのGLプログラムで
 * まとめて描く。1本ごとに`gl.TRIANGLES`で1回のdrawArraysを呼ぶ
 * （本数は多くても数機なので、ドローコールを分けるコストは無視できる）。
 */
export class RidgeLineLayer implements maplibregl.CustomLayerInterface {
  id: string;
  type = 'custom' as const;
  renderingMode = '3d' as const;

  private map: MapLibreMap | null = null;
  private program: WebGLProgram | null = null;
  private matrixLoc: WebGLUniformLocation | null = null;
  private worldSizeLoc: WebGLUniformLocation | null = null;
  private viewportSizeLoc: WebGLUniformLocation | null = null;
  private lineWidthLoc: WebGLUniformLocation | null = null;
  private currentLoc = -1;
  private otherLoc = -1;
  private sideLoc = -1;
  private colorLoc = -1;
  private currentBuffer: WebGLBuffer | null = null;
  private otherBuffer: WebGLBuffer | null = null;
  private sideBuffer: WebGLBuffer | null = null;
  private colorBuffer: WebGLBuffer | null = null;
  private vertexCount = 0;
  private currentData = new Float32Array(0);
  private otherData = new Float32Array(0);
  private sideData = new Float32Array(0);
  private colorData = new Float32Array(0);
  private dirty = false;

  constructor(id: string) {
    this.id = id;
  }

  onAdd(map: MapLibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    this.map = map;
    const vs = compileShader(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    const program = gl.createProgram();
    if (!program) throw new Error('GLプログラムを作成できません');
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      throw new Error(`GLプログラムのリンクに失敗: ${gl.getProgramInfoLog(program)}`);
    }
    this.program = program;
    this.matrixLoc = gl.getUniformLocation(program, 'u_matrix');
    this.worldSizeLoc = gl.getUniformLocation(program, 'u_worldSize');
    this.viewportSizeLoc = gl.getUniformLocation(program, 'u_viewportSize');
    this.lineWidthLoc = gl.getUniformLocation(program, 'u_lineWidthPx');
    this.currentLoc = gl.getAttribLocation(program, 'a_current');
    this.otherLoc = gl.getAttribLocation(program, 'a_other');
    this.sideLoc = gl.getAttribLocation(program, 'a_side');
    this.colorLoc = gl.getAttribLocation(program, 'a_color');
    this.currentBuffer = gl.createBuffer();
    this.otherBuffer = gl.createBuffer();
    this.sideBuffer = gl.createBuffer();
    this.colorBuffer = gl.createBuffer();
  }

  onRemove(_map: MapLibreMap, gl: WebGLRenderingContext | WebGL2RenderingContext): void {
    if (this.program) gl.deleteProgram(this.program);
    if (this.currentBuffer) gl.deleteBuffer(this.currentBuffer);
    if (this.otherBuffer) gl.deleteBuffer(this.otherBuffer);
    if (this.sideBuffer) gl.deleteBuffer(this.sideBuffer);
    if (this.colorBuffer) gl.deleteBuffer(this.colorBuffer);
    this.map = null;
    this.program = null;
    this.currentBuffer = null;
    this.otherBuffer = null;
    this.sideBuffer = null;
    this.colorBuffer = null;
  }

  /**
   * 描く線を丸ごと差し替える。`strips`の1要素が1本の連続した線
   * （フライト1本、またはライブ端末1機ぶん）。2点未満の線は無視する。
   *
   * 区間（隣接2点）ごとに独立した帯（三角形2枚・頂点6個）を組み立てる。
   * ミター（継ぎ目の角処理）はせず単純に重ねるだけ——今回のような細い線
   * （数px）では継ぎ目の隙間・重なりは実用上ほぼ気にならず、複雑な
   * 継ぎ目処理を避けて実装をシンプルに保つ。
   */
  setStrips(strips: RidgeLinePoint[][]): void {
    const current: number[] = [];
    const other: number[] = [];
    const side: number[] = [];
    const colors: number[] = [];

    const toMerc = (p: RidgeLinePoint): [number, number, number] => {
      // ★ zは正規化メルカトル単位（m.z）ではなく、生のメートル高度を渡す
      //   （ファイル冒頭の「重要な落とし穴1.5」参照。modelViewProjectionMatrix
      //   はx・yをworldSize倍のワールドピクセル、zをメートルで受け取る）
      const m = maplibregl.MercatorCoordinate.fromLngLat({ lng: p.lon, lat: p.lat });
      return [m.x, m.y, p.effectiveAltM];
    };

    for (const strip of strips) {
      if (strip.length < 2) continue;
      for (let i = 0; i < strip.length - 1; i++) {
        const a = strip[i];
        const b = strip[i + 1];
        const ma = toMerc(a);
        const mb = toMerc(b);
        // 1区間 = 2枚の三角形 = 6頂点（a側-1, a側+1, b側-1, b側+1, a側+1, b側-1）
        const verts: Array<{ pos: [number, number, number]; other: [number, number, number]; side: number; color: [number, number, number, number] }> = [
          { pos: ma, other: mb, side: -1, color: a.color },
          { pos: ma, other: mb, side: 1, color: a.color },
          { pos: mb, other: ma, side: -1, color: b.color },
          { pos: ma, other: mb, side: 1, color: a.color },
          { pos: mb, other: ma, side: 1, color: b.color },
          { pos: mb, other: ma, side: -1, color: b.color },
        ];
        for (const v of verts) {
          current.push(...v.pos);
          other.push(...v.other);
          side.push(v.side);
          colors.push(...v.color);
        }
      }
    }
    this.currentData = new Float32Array(current);
    this.otherData = new Float32Array(other);
    this.sideData = new Float32Array(side);
    this.colorData = new Float32Array(colors);
    this.vertexCount = side.length;
    this.dirty = true;
  }

  render(gl: WebGLRenderingContext | WebGL2RenderingContext, options: CustomRenderMethodInput): void {
    if (!this.program || !this.map || this.vertexCount === 0) return;
    gl.useProgram(this.program);
    // ★ main.tsのRIDGE_LINE_LIFT_Mで壁の天面よりわずかに高く頂点を置いて
    //   いるので、深度は壁よりわずかに近い側になる。それでもズーム値に
    //   よってはほぼ同値になりうるため、depthFuncをLEQUALにしておく
    //   （既定のLESSだと、同値の深度は先に書き込んだ壁側が勝って線が消える）
    gl.enable(gl.DEPTH_TEST);
    gl.depthFunc(gl.LEQUAL);
    gl.uniformMatrix4fv(this.matrixLoc, false, options.modelViewProjectionMatrix as unknown as Float32Array);
    // ★ modelViewProjectionMatrixはMercatorCoordinateの[0,1]座標をそのまま
    //   受け取らない（ファイル冒頭の「重要な落とし穴1」参照）
    gl.uniform1f(this.worldSizeLoc, this.map.transform.worldSize);
    gl.uniform2f(this.viewportSizeLoc, gl.drawingBufferWidth, gl.drawingBufferHeight);
    gl.uniform1f(this.lineWidthLoc, LINE_WIDTH_PX);

    if (this.dirty) {
      gl.bindBuffer(gl.ARRAY_BUFFER, this.currentBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.currentData, gl.DYNAMIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.otherBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.otherData, gl.DYNAMIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.sideBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.sideData, gl.DYNAMIC_DRAW);
      gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
      gl.bufferData(gl.ARRAY_BUFFER, this.colorData, gl.DYNAMIC_DRAW);
      this.dirty = false;
    }

    gl.bindBuffer(gl.ARRAY_BUFFER, this.currentBuffer);
    gl.enableVertexAttribArray(this.currentLoc);
    gl.vertexAttribPointer(this.currentLoc, 3, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.otherBuffer);
    gl.enableVertexAttribArray(this.otherLoc);
    gl.vertexAttribPointer(this.otherLoc, 3, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.sideBuffer);
    gl.enableVertexAttribArray(this.sideLoc);
    gl.vertexAttribPointer(this.sideLoc, 1, gl.FLOAT, false, 0, 0);

    gl.bindBuffer(gl.ARRAY_BUFFER, this.colorBuffer);
    gl.enableVertexAttribArray(this.colorLoc);
    gl.vertexAttribPointer(this.colorLoc, 4, gl.FLOAT, false, 0, 0);

    gl.drawArrays(gl.TRIANGLES, 0, this.vertexCount);

    gl.disableVertexAttribArray(this.currentLoc);
    gl.disableVertexAttribArray(this.otherLoc);
    gl.disableVertexAttribArray(this.sideLoc);
    gl.disableVertexAttribArray(this.colorLoc);
  }
}
